/**
 * UserPermissionDialog — Rolle, Niederlassungen und Standorte einem User zuweisen.
 */
import React, { useMemo } from 'react';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Loader2, Building, MapPin, Globe, User } from 'lucide-react';
import { PERMISSION_AREAS, LEVEL_LABELS, LEVEL_COLORS } from './constants';
import { formatLastFirst } from '../../lib/nameFormat';

export const UserPermissionDialog = ({
  open, onOpenChange,
  selectedUser, userPermForm, setUserPermForm,
  roles, branches, locations,
  saving, onSave,
}) => {
  const selectedRoleData = useMemo(
    () => roles.find((r) => r.id === userPermForm.role_id),
    [roles, userPermForm.role_id]
  );

  const showScopeChooser =
    selectedRoleData && selectedRoleData.scope_type !== 'own' && selectedRoleData.scope_type !== 'all';

  const toggleId = (key, id) => {
    const list = userPermForm[key];
    const next = list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
    setUserPermForm({ ...userPermForm, [key]: next });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Berechtigungen bearbeiten</DialogTitle>
          <DialogDescription>
            {formatLastFirst(selectedUser)} ({selectedUser?.email})
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6 pt-4">
          <div className="space-y-3">
            <Label className="text-sm font-medium">Rolle *</Label>
            <Select
              value={userPermForm.role_id}
              onValueChange={(v) => {
                const role = roles.find((r) => r.id === v);
                setUserPermForm({
                  ...userPermForm,
                  role_id: v,
                  all_branches: role?.scope_type === 'all',
                  all_locations: role?.scope_type === 'all',
                });
              }}
            >
              <SelectTrigger data-testid="select-role">
                <SelectValue placeholder="Rolle auswaehlen..." />
              </SelectTrigger>
              <SelectContent>
                {roles.map((role) => (
                  <SelectItem key={role.id} value={role.id}>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: role.color }} />
                      <span>{role.name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {selectedRoleData && (
              <div className="bg-muted/50 rounded-lg p-3 space-y-2">
                <p className="text-sm text-muted-foreground">{selectedRoleData.description}</p>
                <div className="flex flex-wrap gap-1">
                  {PERMISSION_AREAS.map((pa) => {
                    const level = selectedRoleData.permissions?.[pa.area] || 'none';
                    if (level === 'none') return null;
                    return (
                      <Badge key={pa.area} variant="secondary" className={`text-xs ${LEVEL_COLORS[level]}`}>
                        {pa.label}: {LEVEL_LABELS[level]}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {showScopeChooser && (
            <div className="space-y-4 border-t pt-4">
              <Label className="text-sm font-medium">Zustaendig fuer</Label>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground flex items-center gap-2">
                    <Building className="h-4 w-4" /> Niederlassungen
                  </span>
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={userPermForm.all_branches}
                      onCheckedChange={(checked) => setUserPermForm({
                        ...userPermForm, all_branches: checked, branch_ids: checked ? [] : userPermForm.branch_ids,
                      })}
                    />
                    Alle
                  </label>
                </div>
                {!userPermForm.all_branches && (
                  <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto p-2 bg-muted/30 rounded-lg">
                    {branches.map((branch) => (
                      <label key={branch.id} className="flex items-center gap-2 text-sm cursor-pointer">
                        <Checkbox
                          checked={userPermForm.branch_ids.includes(branch.id)}
                          onCheckedChange={() => toggleId('branch_ids', branch.id)}
                        />
                        {branch.name}
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground flex items-center gap-2">
                    <MapPin className="h-4 w-4" /> Standorte
                  </span>
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={userPermForm.all_locations}
                      onCheckedChange={(checked) => setUserPermForm({
                        ...userPermForm, all_locations: checked, location_ids: checked ? [] : userPermForm.location_ids,
                      })}
                    />
                    Alle
                  </label>
                </div>
                {!userPermForm.all_locations && (
                  <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto p-2 bg-muted/30 rounded-lg">
                    {locations.map((loc) => (
                      <label key={loc.id} className="flex items-center gap-2 text-sm cursor-pointer">
                        <Checkbox
                          checked={userPermForm.location_ids.includes(loc.id)}
                          onCheckedChange={() => toggleId('location_ids', loc.id)}
                        />
                        {loc.name}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {selectedRoleData?.scope_type === 'all' && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground bg-blue-50 p-3 rounded-lg">
              <Globe className="h-4 w-4 text-blue-500" />
              Diese Rolle hat automatisch Zugriff auf alle Bereiche
            </div>
          )}
          {selectedRoleData?.scope_type === 'own' && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground bg-amber-50 p-3 rounded-lg">
              <User className="h-4 w-4 text-amber-500" />
              Diese Rolle hat nur Zugriff auf eigene Daten
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
            <Button onClick={onSave} disabled={saving || !userPermForm.role_id} data-testid="btn-save-user-perm">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Speichern
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
